import { useState, useEffect } from 'react'
import { lumi } from '../lib/lumi'

export interface Like {
  _id?: string
  projectId: string
  mediaIndex: number
  userId: string
  createdAt?: string
}

export const useLikes = (projectId: string, mediaIndex: number, currentUserId?: string) => {
  const [likes, setLikes] = useState<Like[]>([])
  const [likeCount, setLikeCount] = useState(0)
  const [loading, setLoading] = useState(false)

  const fetchLikes = async () => {
    if (!projectId) return
    
    setLoading(true)
    try {
      const { list } = await lumi.entities.likes.list({
        filter: { projectId, mediaIndex }
      })
      setLikes(list)
      setLikeCount(list.length)
    } catch (error) {
      console.error('Failed to fetch likes:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchLikes()
  }, [projectId, mediaIndex])

  // Function to check if a specific user has liked
  const isLiked = (userId: string): boolean => {
    return likes.some(like => like.userId === userId)
  }

  const toggleLike = async (userId: string) => {
    if (!userId) {
      throw new Error('User must be logged in to like')
    }

    try {
      const userHasLiked = isLiked(userId)
      
      if (userHasLiked) {
        // Unlike
        const userLike = likes.find(like => like.userId === userId)
        if (userLike && userLike._id) {
          await lumi.entities.likes.delete(userLike._id)
        }
      } else {
        // Like
        await lumi.entities.likes.create({
          projectId,
          mediaIndex,
          userId,
          creator: userId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        })
      }
      await fetchLikes()
    } catch (error) {
      console.error('Failed to toggle like:', error)
      throw error
    }
  }

  return {
    likes,
    likeCount,
    isLiked,
    loading,
    toggleLike,
    fetchLikes
  }
}
